import React from 'react';
import { Icon } from './Icon.jsx';
import { enableAnalytics, disableAnalytics } from '../lib/analytics.js';
import { enableMetaPixel, disableMetaPixel } from '../lib/meta-pixel.js';
import { updateGoogleConsent } from '../lib/google-ads.js';

const CONSENT_KEY = 'aa_consent_v1';
const CONSENT_VERSION = 3;
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    if (Date.now() - parsed.ts > CONSENT_TTL_MS) return null;
    return parsed.categories;
  } catch {
    return null;
  }
}

function writeConsent(categories) {
  const payload = { version: CONSENT_VERSION, ts: Date.now(), categories };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  return categories;
}

const listeners = new Set();
function notify(categories) {
  listeners.forEach(fn => { try { fn(categories); } catch (e) { console.error(e); } });
}

let calendlyLoadPromise = null;
export function loadCalendly() {
  if (typeof window !== 'undefined' && window.Calendly) return Promise.resolve();
  if (calendlyLoadPromise) return calendlyLoadPromise;

  calendlyLoadPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') { resolve(); return; }
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://assets.calendly.com/assets/external/widget.css';
    document.head.appendChild(css);

    const js = document.createElement('script');
    js.src = 'https://assets.calendly.com/assets/external/widget.js';
    js.async = true;
    js.onload = () => {
      // window.Calendly is usually available immediately after onload, but poll briefly
      // to handle the rare case where the global hasn't been assigned yet.
      const start = Date.now();
      const check = () => {
        if (window.Calendly) return resolve();
        if (Date.now() - start > 2000) { calendlyLoadPromise = null; return reject(new Error('Calendly init timeout')); }
        setTimeout(check, 40);
      };
      check();
    };
    js.onerror = () => { calendlyLoadPromise = null; reject(new Error('Calendly script failed to load')); };
    document.head.appendChild(js);
  });

  return calendlyLoadPromise;
}

let hubspotLoadPromise = null;
export function loadHubSpot() {
  if (window.hbspt) return Promise.resolve();
  if (hubspotLoadPromise) return hubspotLoadPromise;
  hubspotLoadPromise = new Promise((resolve, reject) => {
    const js = document.createElement('script');
    js.src = 'https://js.hsforms.net/forms/embed/v2.js';
    js.async = true;
    js.charset = 'utf-8';
    js.onload = () => resolve();
    js.onerror = () => { hubspotLoadPromise = null; reject(new Error('HubSpot script failed to load')); };
    document.head.appendChild(js);
  });
  return hubspotLoadPromise;
}

export const AAConsent = {
  has(category) {
    if (category === 'necessary') return true;
    const c = readConsent();
    return !!(c && c[category]);
  },
  set(categories) {
    const merged = { necessary: true, functional: false, statistics: false, marketing: false, ...categories };
    writeConsent(merged);
    notify(merged);
    return merged;
  },
  current() {
    const c = readConsent();
    return c || { necessary: true, functional: false, statistics: false, marketing: false };
  },
  onChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  openSettings() {
    window.dispatchEvent(new CustomEvent('aa:open-cookie-settings'));
  },
};

if (typeof window !== 'undefined') {
  window.AAConsent = AAConsent;
}

(function bootServicesOnConsent() {
  // Google Ads consent: only sync to gtag if the visitor has an EXPLICIT stored
  // choice. For first-time visitors (no stored consent) we must NOT touch the
  // region-scoped defaults set in index.html — otherwise we'd clobber the
  // "granted" default for non-EEA traffic with denied and recreate the 0%
  // consent-rate problem. Fired first so a throwing loader below can't skip it.
  const storedConsent = readConsent();
  if (storedConsent) {
    updateGoogleConsent({
      statistics: !!storedConsent.statistics,
      marketing: !!storedConsent.marketing,
    });
  }
  // Functional: Calendly booking widget + HubSpot guide form
  if (AAConsent.has('functional')) {
    loadCalendly();
    loadHubSpot();
  }
  // Statistics: Firebase Analytics (GA4)
  if (AAConsent.has('statistics')) enableAnalytics();
  // Marketing: Meta Pixel
  if (AAConsent.has('marketing')) enableMetaPixel();

  AAConsent.onChange((c) => {
    updateGoogleConsent({ statistics: !!c.statistics, marketing: !!c.marketing });
    if (c.functional) {
      loadCalendly();
      loadHubSpot();
    }
    if (c.statistics) enableAnalytics();
    else disableAnalytics();
    if (c.marketing) enableMetaPixel();
    else disableMetaPixel();
  });
})();

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`cookie-toggle ${checked ? 'is-on' : ''} ${disabled ? 'is-disabled' : ''}`}
    >
      <span className="cookie-toggle-thumb" />
    </button>
  );
}

export function CookieBanner() {
  const [phase, setPhase] = React.useState('hidden');
  const [draft, setDraft] = React.useState(() => AAConsent.current());

  React.useEffect(() => {
    const stored = readConsent();
    if (!stored) {
      const t = setTimeout(() => setPhase('banner'), 400);
      return () => clearTimeout(t);
    }
  }, []);

  React.useEffect(() => {
    const open = () => {
      setDraft(AAConsent.current());
      setPhase('settings');
    };
    window.addEventListener('aa:open-cookie-settings', open);
    return () => window.removeEventListener('aa:open-cookie-settings', open);
  }, []);

  React.useEffect(() => {
    if (phase !== 'settings') return;
    const onKey = (e) => { if (e.key === 'Escape') setPhase('hidden'); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [phase]);

  const acceptAll = () => { AAConsent.set({ functional: true, statistics: true, marketing: true }); setPhase('hidden'); };
  const rejectAll = () => { AAConsent.set({ functional: false, statistics: false, marketing: false }); setPhase('hidden'); };
  const saveSelection = () => { AAConsent.set(draft); setPhase('hidden'); };

  if (phase === 'hidden') return null;

  return (
    <>
      {phase === 'banner' && (
        <div className="cookie-banner" role="dialog" aria-live="polite" aria-labelledby="cookie-banner-title">
          <div className="cookie-banner-inner">
            <div className="cookie-banner-text">
              <h2 id="cookie-banner-title" className="cookie-banner-title">Wir respektieren deine Privatsphäre</h2>
              <p className="cookie-banner-body">
                Wir verwenden Cookies und ähnliche Technologien. Notwendige Cookies sind für den Betrieb der Seite erforderlich.
                Funktionale&nbsp;(Calendly, HubSpot), Statistik-&nbsp;(Google&nbsp;Analytics) und Marketing-Cookies&nbsp;(Meta&nbsp;Pixel, Google&nbsp;Ads)
                laden wir nur mit deiner Zustimmung. Du kannst deine Auswahl jederzeit im Footer unter „Cookie-Einstellungen" ändern.
                Details findest du in unserer{' '}
                <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutzerklärung</a>.
              </p>
            </div>
            <div className="cookie-banner-actions">
              <button className="cookie-btn cookie-btn-ghost" onClick={() => { setDraft(AAConsent.current()); setPhase('settings'); }}>
                Einstellungen
              </button>
              <button className="cookie-btn cookie-btn-secondary" onClick={rejectAll}>
                Alle ablehnen
              </button>
              <button className="cookie-btn cookie-btn-primary" onClick={acceptAll}>
                Alle akzeptieren
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === 'settings' && (
        <div className="cookie-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setPhase('hidden'); }}>
          <div className="cookie-modal" role="dialog" aria-modal="true" aria-labelledby="cookie-modal-title">
            <div className="cookie-modal-header">
              <h2 id="cookie-modal-title" className="cookie-modal-title">Cookie-Einstellungen</h2>
              <button className="cookie-modal-close" aria-label="Schließen" onClick={() => setPhase('hidden')}>
                <Icon.X size={20} />
              </button>
            </div>

            <div className="cookie-modal-body">
              <p className="cookie-modal-intro">
                Wähle, welche Cookies du zulassen möchtest. Notwendige Cookies sind immer aktiv, da die Seite ohne sie nicht
                funktioniert. Deine Auswahl gilt für 12 Monate und kann jederzeit geändert werden.
              </p>

              <div className="cookie-category">
                <div className="cookie-category-row">
                  <div>
                    <div className="cookie-category-title">Notwendig</div>
                    <div className="cookie-category-desc">
                      Erforderlich für den Betrieb der Website (z.&nbsp;B. Speicherung deiner Cookie-Einstellungen). Diese Cookies können
                      nicht deaktiviert werden.
                    </div>
                  </div>
                  <Toggle checked={true} disabled={true} label="Notwendig (immer aktiv)" onChange={() => {}} />
                </div>
                <details className="cookie-category-details">
                  <summary>Details</summary>
                  <ul>
                    <li><strong>aa_consent_v1</strong>: speichert deine Cookie-Einstellungen (Local Storage, 12 Monate, Erstanbieter)</li>
                    <li><strong>aa_guide_leads</strong>: temporäre Speicherung deiner Guide-Anfrage (Local Storage, Erstanbieter)</li>
                  </ul>
                </details>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-row">
                  <div>
                    <div className="cookie-category-title">Funktional</div>
                    <div className="cookie-category-desc">
                      Lädt externe Widgets, die du aktiv nutzt: das Calendly-Buchungs-Widget für Termine und das HubSpot-Formular
                      für den Guide-Download. Beide Dienste haben Sitz in den USA; bei Aktivierung werden Daten dorthin übertragen.
                    </div>
                  </div>
                  <Toggle
                    checked={draft.functional}
                    label="Funktional aktivieren"
                    onChange={(v) => setDraft({ ...draft, functional: v })}
                  />
                </div>
                <details className="cookie-category-details">
                  <summary>Details</summary>
                  <ul>
                    <li><strong>Calendly</strong>: Buchungs-Widget; setzt Cookies von <code>calendly.com</code>; Datenübertragung in die USA. Mehr Infos in der <a href="https://calendly.com/de/pages/privacy" target="_blank" rel="noopener">Calendly-Datenschutzerklärung</a>.</li>
                    <li><strong>HubSpot Forms</strong>: Lead-Formular für den Guide-Download; setzt <code>__hstc</code>, <code>__hssc</code>, <code>hubspotutk</code> Cookies (Laufzeit bis zu 13 Monaten); Datenübertragung in die USA. Mehr Infos in der <a href="https://legal.hubspot.com/de/privacy-policy" target="_blank" rel="noopener">HubSpot-Datenschutzerklärung</a>.</li>
                  </ul>
                </details>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-row">
                  <div>
                    <div className="cookie-category-title">Statistik</div>
                    <div className="cookie-category-desc">
                      Anonyme Nutzungsstatistik mit Google&nbsp;Analytics&nbsp;4 (über Firebase), damit wir die Website verbessern können.
                      Setzt Cookies von Google&nbsp;LLC (USA). Wir nutzen die Daten nicht für personalisierte Werbung.
                    </div>
                  </div>
                  <Toggle
                    checked={draft.statistics}
                    label="Statistik aktivieren"
                    onChange={(v) => setDraft({ ...draft, statistics: v })}
                  />
                </div>
                <details className="cookie-category-details">
                  <summary>Details</summary>
                  <ul>
                    <li><strong>Google Analytics 4</strong> (via Firebase, Mess-ID <code>G-0590H4LCFP</code>): anonymisierte Nutzungsanalyse. Setzt <code>_ga</code> und <code>_ga_*</code> Cookies (Laufzeit bis zu 24 Monaten). Daten werden in die USA übertragen. Mehr Infos in der <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Google-Datenschutzerklärung</a>.</li>
                  </ul>
                </details>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-row">
                  <div>
                    <div className="cookie-category-title">Marketing</div>
                    <div className="cookie-category-desc">
                      Misst die Wirksamkeit unserer Werbeanzeigen auf Facebook/Instagram (Meta Pixel) sowie Google&nbsp;Ads. Hilft uns, dir relevantere
                      Inhalte zu zeigen und unsere Marketing-Ausgaben effizient einzusetzen. Setzt Cookies von Meta Platforms, Inc. und Google&nbsp;LLC (USA).
                    </div>
                  </div>
                  <Toggle
                    checked={draft.marketing}
                    label="Marketing aktivieren"
                    onChange={(v) => setDraft({ ...draft, marketing: v })}
                  />
                </div>
                <details className="cookie-category-details">
                  <summary>Details</summary>
                  <ul>
                    <li><strong>Meta Pixel</strong> (Pixel-ID <code>1461046241681125</code>): Conversion-Tracking und Retargeting für Facebook- und Instagram-Anzeigen. Setzt <code>_fbp</code> Cookie (Laufzeit 90&nbsp;Tage) sowie <code>_fbc</code> beim Klick auf eine Werbeanzeige. Daten werden an Meta Platforms, Inc. (USA) übertragen. Mehr Infos in der <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener">Meta-Datenschutzrichtlinie</a>.</li>
                    <li><strong>Google Ads</strong> (Conversion-ID <code>AW-18148879279</code>, gtag.js mit Google&nbsp;Consent&nbsp;Mode&nbsp;v2): Conversion-Tracking und Remarketing für Google-Ads-Kampagnen. Setzt nach deiner Zustimmung <code>_gcl_*</code> Cookies (Laufzeit bis zu 90&nbsp;Tagen). Ohne Zustimmung sendet der Tag nur cookielose Ping-Signale ohne Identifier. Daten werden an Google&nbsp;LLC (USA) übertragen. Mehr Infos in der <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Google-Datenschutzerklärung</a>.</li>
                  </ul>
                </details>
              </div>
            </div>

            <div className="cookie-modal-footer">
              <button className="cookie-btn cookie-btn-secondary" onClick={rejectAll}>Alle ablehnen</button>
              <button className="cookie-btn cookie-btn-ghost" onClick={saveSelection}>Auswahl speichern</button>
              <button className="cookie-btn cookie-btn-primary" onClick={acceptAll}>Alle akzeptieren</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

